import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IPricingStrategyEntityAttributes extends IModelAttributes {
	enabled: boolean;
	name: string;
	description: string;

	passengerPricingStrategyProfiles: Array<
		Models.PricingStrategyProfileEntity | Models.IPricingStrategyProfileEntityAttributes
	>;
	vehiclePricingStrategyProfiles: Array<
		Models.PricingStrategyProfileEntity | Models.IPricingStrategyProfileEntityAttributes
	>;
	freightPricingStrategyProfiles: Array<
		Models.PricingStrategyProfileEntity | Models.IPricingStrategyProfileEntityAttributes
	>;
	prices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
}

@entity('PricingStrategyEntity', 'PricingStrategy')
export default class PricingStrategyEntity extends Model implements IPricingStrategyEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	constructor(attributes?: Partial<IPricingStrategyEntityAttributes>) {
		super(attributes);

		this.passengerPricingStrategyProfiles = [];
		this.vehiclePricingStrategyProfiles = [];
		this.freightPricingStrategyProfiles = [];
		this.prices = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	@observable
	@attribute()
	public enabled: boolean;

	@observable
	@attribute()
	public name: string;

	@observable
	@attribute()
	public description: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public passengerPricingStrategyProfiles: Models.PricingStrategyProfileEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public vehiclePricingStrategyProfiles: Models.PricingStrategyProfileEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightPricingStrategyProfiles: Models.PricingStrategyProfileEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public prices: Models.PriceEntity[] = [];

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IPricingStrategyEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.enabled !== undefined) {
				this.enabled = attributes.enabled;
			}
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (
				attributes.passengerPricingStrategyProfiles !== undefined &&
				Array.isArray(attributes.passengerPricingStrategyProfiles)
			) {
				if (!this.passengerPricingStrategyProfiles) {
					this.passengerPricingStrategyProfiles = [];
				}
				for (const model of attributes.passengerPricingStrategyProfiles) {
					if (model instanceof Models.PricingStrategyProfileEntity) {
						this.passengerPricingStrategyProfiles.push(model);
					} else {
						this.passengerPricingStrategyProfiles.push(new Models.PricingStrategyProfileEntity(model));
					}
				}
			}
			if (
				attributes.vehiclePricingStrategyProfiles !== undefined &&
				Array.isArray(attributes.vehiclePricingStrategyProfiles)
			) {
				if (!this.vehiclePricingStrategyProfiles) {
					this.vehiclePricingStrategyProfiles = [];
				}
				for (const model of attributes.vehiclePricingStrategyProfiles) {
					if (model instanceof Models.PricingStrategyProfileEntity) {
						this.vehiclePricingStrategyProfiles.push(model);
					} else {
						this.vehiclePricingStrategyProfiles.push(new Models.PricingStrategyProfileEntity(model));
					}
				}
			}
			if (
				attributes.freightPricingStrategyProfiles !== undefined &&
				Array.isArray(attributes.freightPricingStrategyProfiles)
			) {
				if (!this.freightPricingStrategyProfiles) {
					this.freightPricingStrategyProfiles = [];
				}
				for (const model of attributes.freightPricingStrategyProfiles) {
					if (model instanceof Models.PricingStrategyProfileEntity) {
						this.freightPricingStrategyProfiles.push(model);
					} else {
						this.freightPricingStrategyProfiles.push(new Models.PricingStrategyProfileEntity(model));
					}
				}
			}
			if (attributes.prices !== undefined && Array.isArray(attributes.prices)) {
				if (!this.prices) {
					this.prices = [];
				}
				for (const model of attributes.prices) {
					if (model instanceof Models.PriceEntity) {
						this.prices.push(model);
					} else {
						this.prices.push(new Models.PriceEntity(model));
					}
				}
			}
		}
	}
}
